import styled from 'styled-components/macro'
import { useEffect, useMemo, useState } from 'react'
import ReactSelectWrapper from '../FormUtils/ReactSelectWrapper'
import FbButton from 'components/FbUI/FbButton'
import Autocomplete from 'components/FormUtils/Autocomplete'
import useCachedState from 'utils/useCachedState'
import { FilterStore, SearchFilterState } from 'components/Filters/FilterStore'
import { useAuth } from '../../context/authentication/useAuth'

interface IElastiSearchHistory {
  [key: string]: string[]
}

interface IOption {
  label: string
  searchKey: string
  searchPlaceholder?: string
  elastic?: boolean
}

export function TableSearchWithElastic({
  options,
  historyKey,
  filterStore,
}: {
  options: IOption[]
  historyKey: string
  filterStore: FilterStore
}) {
  const { user } = useAuth()
  const LOCAL_STORAGE_KEY_PREFIX = `5-${user?.id}-`
  const { search, setSearch } = filterStore()

  const selectOptions = useMemo(() => {
    return options.map((option) => ({
      label: option.label,
      value: option.searchKey,
      searchKey: option.searchKey,
      searchPlaceholder: option.searchPlaceholder,
      elastic: option.elastic,
    }))
  }, [options])

  const initialOption =
    selectOptions?.find((option) =>
      Object.keys(search).includes(option.searchKey)
    ) ?? options?.[0]

  const [localSearch, setLocalSearch] = useState<SearchFilterState>(search)
  const [selectedOption, setSelectedOption] = useState<IOption>(initialOption)
  const [searchHistory, setSearchHistory] =
    useCachedState<IElastiSearchHistory>(
      {},
      LOCAL_STORAGE_KEY_PREFIX + 'search-history-' + historyKey
    )

  useEffect(() => {
    // Reset local search when elastiSearch or selectedOption changes
    setLocalSearch(search)
  }, [search, selectedOption])

  const applySearch = () => {
    const currentSearchKey = selectedOption.searchKey
    const currentSearchValue = localSearch[currentSearchKey]

    // Add to search history for current key
    if (currentSearchValue?.value) {
      if (!searchHistory[currentSearchKey]) {
        searchHistory[currentSearchKey] = []
      }
      if (
        !searchHistory[currentSearchKey]
          .map((s) => s.toLowerCase())
          .includes(currentSearchValue.value.toLowerCase())
      ) {
        // Insert at the beginning
        searchHistory[currentSearchKey].unshift(currentSearchValue.value)
      }
      // Limit to last 15 searches
      if (searchHistory[currentSearchKey].length > 15) {
        searchHistory[currentSearchKey].splice(
          0,
          searchHistory[currentSearchKey].length - 15
        )
      }

      setSearchHistory((prev) => ({
        ...prev,
        [currentSearchKey]: searchHistory[currentSearchKey],
      }))
    }

    // Clear all search filters first (enforce only one active at a time)
    options.forEach((option) => {
      if (option.searchKey !== currentSearchKey) {
        setSearch(option.searchKey, '', '', option.elastic ?? false)
      }
    })

    // Apply only the current search
    if (currentSearchValue?.value) {
      setSearch(
        currentSearchKey,
        currentSearchValue.value,
        currentSearchValue.label ?? selectedOption.label,
        currentSearchValue.elastic ?? false
      )
    }
  }

  return (
    <div className="flex flex-1">
      <Dropdown
        options={selectOptions}
        value={selectedOption}
        isSearchable={false}
        onChange={(option) => {
          if (option) {
            console.log(option)
            const newOption = option as IOption
            setSelectedOption(newOption)
            // Clear all other search keys from local state (only keep current)
            setLocalSearch((prev) => {
              const newSearch: SearchFilterState = {}
              if (prev[newOption.searchKey]) {
                newSearch[newOption.searchKey] = prev[newOption.searchKey]
              }
              return newSearch
            })
          }
        }}
        menuPosition="fixed"
        styles={{
          control: () => ({
            height: '100%',
            minWidth: 100,
            borderRadius: '6px 0 0 6px',
            cursor: 'pointer',
            borderRight: 'none',
          }),
          indicatorSeparator: () => ({
            display: 'none',
          }),
          valueContainer: (provided) => ({
            ...provided,
            padding: '2px 8px 2px 12px',
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            padding: '8px 8px 8px 0',
            color: '#343741',
            '&:hover': {
              color: '#272a31',
            },
          }),
        }}
      />
      <form
        className="flex justify-between w-full"
        onSubmit={(e) => {
          e.preventDefault()
          applySearch()
        }}
      >
        <Autocomplete
          type="text"
          options={searchHistory[selectedOption.searchKey] ?? []}
          value={localSearch[selectedOption.searchKey]?.value ?? ''}
          onChange={(e) =>
            setLocalSearch((prev) => ({
              ...prev,
              [selectedOption.searchKey]: {
                value: e.target.value.replaceAll(/[;:]/g, ''),
                label: selectedOption.label,
                elastic: selectedOption.elastic,
              },
            }))
          }
          placeholder={selectedOption.searchPlaceholder}
          className="form-control flex-1 min-w-[150px]"
          style={{ borderRadius: '0 6px 6px 0', flex: 1 }}
        />
        <FbButton
          className="-ml-1 h-full font-semibold rounded-tr-lg rounded-br-lg rounded-tl-none rounded-bl-none"
          onClick={applySearch}
        >
          Search
        </FbButton>
      </form>
    </div>
  )
}

export const SearchButton = styled(FbButton)`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  height: 100%;
  font-size: 1rem;
  line-height: normal;
  border-radius: 0 6px 6px 0;
  font-weight: 600 !important;
`

export const Dropdown = styled(ReactSelectWrapper)`
  & > .form-control-select {
    background: #eef0f1;
    color: #343741 !important;
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 600;
  }
`
